import React, { useState, useEffect, useRef } from "react";
import { Dialog } from "@headlessui/react";
import { Input } from "@/components/input";
import { Button } from "@/components/button";
import { Badge } from "@/components/badge";
import { XMarkIcon } from "@heroicons/react/20/solid";
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableHeader,
  TableCell,
} from "@/components/table";
import { PaginationComponent } from "@/components/PaginationComponent";
import { useExamPaperList } from "@/apis/exam_papers/getExamPaperList";
import { getPaperTypeLabel } from "@/constants/exam";
import type { IExamPaper } from "@/apis/exam_papers/types";

interface ExamPaperSelectModalProps {
  open: boolean;
  onClose: () => void;
  onSelect: (paper: IExamPaper) => void;
  excludeIds?: string[];
  existingTypes?: string[];
  examType?: string;
}

const PAGE_SIZE = 10;

const ExamPaperSelectModal: React.FC<ExamPaperSelectModalProps> = ({
  open,
  onClose,
  onSelect,
  excludeIds = [],
  existingTypes = [],
  examType,
}) => {
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const debounceTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Debounce search
  useEffect(() => {
    if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
    debounceTimeout.current = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 300);
    // eslint-disable-next-line
  }, [search]);

  // Fetch exam papers with type filtering at API level
  const getAvailableTypes = () => {
    if (examType !== "SAT") {
      return undefined; // 모든 Paper fetch
    }
    
    // SAT인 경우만 기존 로직 적용
    const hasReadingWriting = existingTypes.includes("reading_writing");
    const hasMath = existingTypes.includes("math");
    
    if (hasReadingWriting && hasMath) {
      return null; // 둘 다 있으면 더 이상 추가 불가
    } else if (hasReadingWriting) {
      return "math"; // Reading Writing만 있으면 Math만 fetch
    } else if (hasMath) {
      return "reading_writing"; // Math만 있으면 Reading Writing만 fetch
    } else {
      return undefined; // 아무것도 없으면 모든 Paper fetch
    }
  };

  const availableType = getAvailableTypes();

  const { data, isLoading } = useExamPaperList(
    availableType === null
      ? { page: 1, limit: 0 }
      : {
          page,
          limit: PAGE_SIZE,
          title: debouncedSearch,
          subject: availableType,
          exam_type: examType,
        }
  );
  const examPapers: IExamPaper[] = data?.items || [];
  const totalCount = data?.totalCount || 0;
  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  // Only filter by excludeIds now (type filtering handled by API)
  const availableExamPapers = examPapers.filter(
    (p) => !excludeIds.includes(p.id)
  );

  // Handle select
  const handleSelect = (paper: IExamPaper) => {
    onSelect(paper);
    onClose();
    setSearch("");
    setPage(1);
  };

  // Reset state when closed
  useEffect(() => {
    if (!open) {
      setSearch("");
      setDebouncedSearch("");
      setPage(1);
    }
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      className="fixed z-50 inset-0 overflow-y-auto"
    >
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="fixed inset-0 bg-black opacity-30" aria-hidden="true" />
        <div className="relative bg-white rounded-lg shadow-xl w-full max-w-4xl mx-auto p-8 z-10">
          <Dialog.Title className="text-lg font-semibold mb-4 flex items-center justify-between">
            <span>Add Exam Paper</span>
            <button
              type="button"
              className="ml-auto text-zinc-400 hover:text-zinc-700"
              onClick={onClose}
              aria-label="Close"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </Dialog.Title>
          <form
            className="mb-4 flex gap-2 items-center"
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setDebouncedSearch(search);
              setPage(1);
            }}
          >
            <Input
              placeholder="Search exam papers..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="max-w-xs"
            />
            <Button type="submit" className="h-10 px-4">
              Search
            </Button>
          </form>
          <div className="overflow-x-auto">
            <Table className="min-w-[900px]">
              <TableHead>
                <TableRow>
                  <TableHeader>Title</TableHeader>
                  <TableHeader>Subject</TableHeader>
                  <TableHeader>Section</TableHeader>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={3} className="text-center py-8">
                      Loading exam papers...
                    </TableCell>
                  </TableRow>
                ) : availableExamPapers.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={3}
                      className="text-center py-8 text-zinc-400"
                    >
                      No exam papers found.
                    </TableCell>
                  </TableRow>
                ) : (
                  availableExamPapers.map((paper) => {
                    const isSAT = paper.exam_type === "SAT";

                    return (
                      <TableRow
                        key={paper.id}
                        onClick={() => handleSelect(paper)}
                        className="cursor-pointer hover:bg-blue-50 transition"
                      >
                        <TableCell>
                          <div className="font-medium text-gray-900">
                            {paper.title}
                          </div>
                          <div className="text-xs text-zinc-500 mt-0.5">
                            {paper.exam_type}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge color="zinc" className="text-xs">
                            {getPaperTypeLabel(paper.subject)}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="space-y-2">
                            {paper.sections?.map((section, index) => {
                              const sectionLabel = isSAT
                                ? `M${index + 1}`
                                : `${index + 1}`;
                              const questionCount =
                                section.question_ids?.length || 0;
                              const duration = section.duration || 0;
                              const showBadge =
                                (paper.sections?.length || 0) > 1;

                              return (
                                <div
                                  key={index}
                                  className="flex items-center gap-2"
                                >
                                  {showBadge && (
                                    <Badge color="zinc" className="text-xs">
                                      {sectionLabel}
                                    </Badge>
                                  )}
                                  <div className="text-xs text-zinc-500">
                                    {questionCount}Q • {duration}min
                                  </div>
                                </div>
                              );
                            }) || (
                              <div className="text-xs text-zinc-400">
                                No sections
                              </div>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
          <div className="mt-6 flex justify-end">
            <PaginationComponent
              currentPage={page}
              totalPages={totalPages}
              hasNextPage={page < totalPages}
              hasPrevPage={page > 1}
              onPageChange={setPage}
            />
          </div>
        </div>
      </div>
    </Dialog>
  );
};

export default ExamPaperSelectModal;
